import type { CSSProperties } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  /** Current selected value */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Available options */
  options: SelectOption[];
  /** Placeholder text when no value selected */
  placeholder?: string;
  /** Label for the select */
  label?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Whether the select is in an error state */
  error?: boolean;
  /** Error message to display */
  errorMessage?: string;
}

/**
 * Select dropdown component.
 *
 * Features:
 * - Custom styled dropdown
 * - Optional label
 * - Error state support
 * - Accessible with proper ARIA labels
 */
export const Select = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  label,
  disabled = false,
  error = false,
  errorMessage,
}: SelectProps) => {
  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    width: '100%',
  };

  const labelStyle: CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: '#232730',
  };

  const selectStyle: CSSProperties = {
    padding: '10px 40px 10px 12px',
    fontSize: '14px',
    color: value ? '#232730' : '#9CA1AB',
    backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
    border: `1px solid ${error ? '#E11D48' : '#D1D5DB'}`,
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23${disabled ? '9CA3AF' : '121212'}' d='M6 8L2 4h8z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    outline: 'none',
    transition: 'all 200ms',
  };

  const errorMessageStyle: CSSProperties = {
    fontSize: '13px',
    color: '#E11D48',
  };

  return (
    <div style={containerStyle}>
      {label && <label style={labelStyle}>{label}</label>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={selectStyle}
        onFocus={(e) => {
          if (!disabled && !error) {
            e.currentTarget.style.borderColor = '#510CFF';
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(81, 12, 255, 0.1)';
          }
        }}
        onBlur={(e) => {
          if (!disabled && !error) {
            e.currentTarget.style.borderColor = '#D1D5DB';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
        aria-label={label}
        aria-invalid={error}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && errorMessage && <span style={errorMessageStyle}>{errorMessage}</span>}
    </div>
  );
};
